import { useState, useEffect, useRef } from "react";
import { Megaphone, Search, Loader2, UserRound, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { STAGES, STAGE_KEYS, GRADES_BY_STAGE, sectionsForGrade } from "../lib/constants";
import { pickupTeacherStudents, pickupConfirmExit } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function PickupTeacher() {
  const { user } = useAuth();
  const [stage, setStage] = useState("");
  const [grade, setGrade] = useState("");
  const [section, setSection] = useState("");
  const [students, setStudents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const allowedStages = user?.role === "admin" || user?.role === "super_admin" || !user?.assigned_stages?.length
    ? STAGE_KEYS : STAGE_KEYS.filter(s => user.assigned_stages.includes(s));
  const grades = stage ? GRADES_BY_STAGE[stage] : [];
  const sections = sectionsForGrade(grade);

  const stageRef = useRef(); const gradeRef = useRef(); const sectionRef = useRef();
  stageRef.current = stage; gradeRef.current = grade; sectionRef.current = section;

  const doSearch = async (silent = false) => {
    const s = stageRef.current, g = gradeRef.current, sec = sectionRef.current;
    if (!s || !g || !sec) {
      if (!silent) toast.error("اختر المرحلة والصف والفصل");
      return;
    }
    if (!silent) setLoading(true);
    try {
      const data = await pickupTeacherStudents({ stage: s, grade: g, section: sec });
      setStudents(data);
    } catch (err) {
      if (!silent) toast.error(err?.response?.data?.detail || "تعذر التحميل");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      console.log("PICKUP_TICK before-call", stageRef.current, gradeRef.current, sectionRef.current);
      if (stageRef.current && gradeRef.current && sectionRef.current) {
        console.log("PICKUP_TICK calling doSearch now...");
        doSearch(true).then(() => console.log("PICKUP_TICK doSearch finished")).catch((e) => console.log("PICKUP_TICK doSearch threw", e));
      } else {
        console.log("PICKUP_TICK condition FALSE, skipping");
      }
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  const doConfirmExit = async () => {
    if (!selected?.pickup_request) return;
    setConfirming(true);
    try {
      await pickupConfirmExit(selected.pickup_request.id);
      toast.success("تم تسجيل خروج الطالب");
      setSelected(null);
      await doSearch();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر التنفيذ");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div>
      <PageHeader title="استدعاء الطالب" subtitle="اختر الفصل لعرض بطاقات الطلاب، والطلاب المستدعون يظهرون أولًا باللون الأحمر" icon={Megaphone} />

      <Card className="p-4 sm:p-5 border-slate-200 mb-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label>المرحلة</Label>
            <Select value={stage} onValueChange={(v) => { setStage(v); setGrade(""); setSection(""); setStudents(null); }}>
              <SelectTrigger className="h-10" dir="rtl"><SelectValue placeholder="اختر" /></SelectTrigger>
              <SelectContent dir="rtl">{allowedStages.map(k => <SelectItem key={k} value={k}>{STAGES[k]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>الصف</Label>
            <Select value={grade} onValueChange={(v) => { setGrade(v); setSection(""); setStudents(null); }} disabled={!stage}>
              <SelectTrigger className="h-10" dir="rtl"><SelectValue placeholder={stage ? "اختر" : "..."} /></SelectTrigger>
              <SelectContent dir="rtl">{grades.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>الفصل</Label>
            <Select value={section} onValueChange={setSection} disabled={!grade}>
              <SelectTrigger className="h-10" dir="rtl"><SelectValue placeholder={grade ? "اختر" : "..."} /></SelectTrigger>
              <SelectContent dir="rtl">{sections.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={doSearch} disabled={loading} className="h-10 px-5 bg-sky-700 hover:bg-sky-800 text-white gap-2 btn-elevate">
            {loading ? <Loader2 className="animate-spin" size={16} /> : <Search size={16} />} بحث
          </Button>
        </div>
      </Card>

      {students && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {students.length === 0 && (
            <div className="col-span-full text-center py-12 text-slate-400">لا يوجد طلاب في هذا الفصل.</div>
          )}
          {students.map((s) => {
            const req = s.pickup_request;
            const called = !!req;
            const exited = called && (req.status === "exited" || req.status === "on_the_way");
            const waiting = called && !exited; // status === "new"
            return (
              <button
                key={s.id}
                onClick={() => waiting && setSelected(s)}
                className={`rounded-2xl border p-4 text-right transition-all ${
                  waiting
                    ? "bg-rose-50 border-rose-300 ring-1 ring-rose-200 hover:shadow-md cursor-pointer"
                    : exited
                    ? "bg-emerald-50 border-emerald-300 ring-1 ring-emerald-200 cursor-default"
                    : "bg-white border-slate-200 cursor-default"
                }`}
                data-testid={`pickup-student-${s.id}`}
              >
                <div className={`w-14 h-14 rounded-xl mx-auto flex items-center justify-center mb-2 ${waiting ? "bg-rose-100" : exited ? "bg-emerald-100" : "bg-slate-50"}`}>
                  <UserRound size={26} className={waiting ? "text-rose-600" : exited ? "text-emerald-600" : "text-slate-400"} />
                </div>
                <div className={`font-cairo font-bold text-sm text-center ${waiting ? "text-rose-700" : exited ? "text-emerald-700" : "text-slate-800"}`}>
                  {waiting && "🔴 "}{exited && "🟢 "}{s.name}
                </div>
                <div className="text-[11px] text-slate-500 text-center mt-1">{s.grade} {s.section && `— ${s.section}`}</div>
                <div className={`mt-2 text-center text-[11px] font-bold ${waiting ? "text-rose-600" : exited ? "text-emerald-600" : "text-slate-400"}`}>
                  {waiting ? "مطلوب استدعاؤه" : exited ? "خرج من الفصل" : "طبيعي"}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-cairo font-bold text-slate-900">بيانات الاستدعاء</h3>
              <button onClick={() => setSelected(null)}><X size={18} className="text-slate-400" /></button>
            </div>
            <div className="space-y-2 text-sm">
              <div><span className="text-slate-500">الطالب:</span> <strong>{selected.name}</strong></div>
              <div><span className="text-slate-500">الصف/الفصل:</span> {selected.grade} — {selected.section}</div>
              <div><span className="text-slate-500">ولي الأمر:</span> {selected.pickup_request?.guardian_name}</div>
              <div><span className="text-slate-500">رقم الطلب:</span> <span className="tabular-nums">{selected.pickup_request?.request_number}</span></div>
              <div><span className="text-slate-500">وقت الطلب:</span> {new Date(selected.pickup_request?.requested_at).toLocaleTimeString("ar-SA")}</div>
            </div>
            <Button onClick={doConfirmExit} disabled={confirming} className="w-full h-11 mt-5 gap-2 bg-emerald-600 hover:bg-emerald-700 text-white">
              {confirming ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />} تم خروج الطالب من الفصل
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
